import type { Substance } from '../pharmacology/types';
import { computeBrainConcentrationNM } from '../simulation/pkModel';
import { requireElement } from './domHelpers';

const TIME_WINDOW_HOURS = 24;
const SAMPLE_COUNT = 120;

/** Canvas-drawn live brain-concentration curve over the 0-24h time window, with a marker at the current time. */
export class ConcentrationGraph {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  constructor() {
    this.canvas = requireElement<HTMLCanvasElement>('graph-canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Could not acquire 2D context for graph canvas');
    this.ctx = ctx;
  }

  render(substance: Substance | null, doseOrReleaseLevel: number, currentTHours: number, color: string): void {
    const { ctx, canvas } = this;
    const width = canvas.width;
    const height = canvas.height;
    const padding = 10;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#0a0f1a';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#1a2840';
    ctx.strokeRect(0.5, 0.5, width - 1, height - 1);

    if (!substance || doseOrReleaseLevel <= 0) {
      ctx.fillStyle = '#5f7b9c';
      ctx.font = '11px Inter, system-ui, sans-serif';
      ctx.fillText('Select a substance to see its concentration curve', padding, height / 2);
      return;
    }

    const samples: number[] = [];
    let maxConcentration = 0;
    for (let i = 0; i <= SAMPLE_COUNT; i++) {
      const t = (i / SAMPLE_COUNT) * TIME_WINDOW_HOURS;
      const c = computeBrainConcentrationNM(substance.pk, doseOrReleaseLevel, t);
      samples.push(c);
      if (c > maxConcentration) maxConcentration = c;
    }
    if (maxConcentration <= 0) maxConcentration = 1;

    const plotWidth = width - padding * 2;
    const plotHeight = height - padding * 2;

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    samples.forEach((c, i) => {
      const x = padding + (i / SAMPLE_COUNT) * plotWidth;
      const y = padding + plotHeight - (c / maxConcentration) * plotHeight;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    const markerX = padding + (currentTHours / TIME_WINDOW_HOURS) * plotWidth;
    ctx.strokeStyle = '#ffffff55';
    ctx.beginPath();
    ctx.moveTo(markerX, padding);
    ctx.lineTo(markerX, padding + plotHeight);
    ctx.stroke();

    ctx.fillStyle = '#8ab4d8';
    ctx.font = '10px Inter, system-ui, sans-serif';
    ctx.fillText(`Brain conc. (nM), 0-${TIME_WINDOW_HOURS}h`, padding, 12);
    ctx.fillText(`max ${maxConcentration.toFixed(1)} nM`, padding, height - 4);
  }
}
